import { useCallback, useEffect, useState } from "react"
import { dateTimeStampFormat } from "shared/util"

import { useUserSession } from "features/session/context"
import { useSearch } from "features/search/context"
import {
	KojinResult,
	KojinSummaryResult,
	useGetKojinSummary,
	usePostKojinSummary,
	usePostKojinSummaryGenerativeAI
} from "interface/kojin"

import {
	Box, BoxProps, Stack,
	Button, IconButton,
	Typography,
	StackProps
} from "@mui/material"
import {
  Assistant as AIIcon
} from '@mui/icons-material'
import { EditIcon, OkIcon, CloseIcon } from "shared/icons"

import { MarkdownPreview } from "components/controls/markDownPreview"
import { MarkdownInput } from "components/controls/markDownInput"
import { ErrorBlock } from "components/controls/errorInfo"
import { BlockCaption } from "components/controls/blockCaption"

export const useKojinSummary = (
	kojin: KojinResult | null
) => {
	const search = useSearch()
	const { isGeneral, hasGenAI } = useUserSession()

	const [isEditMode, setEditMode] = useState<boolean>(false)

	const [summaryData, setSummaryData] = useState<{
		summary: string
		updateTime?: string
	} | null>(null)

	const [editData, setEditData] = useState<string>("")

	const getState = useGetKojinSummary(search?.searchId, kojin?.kojinCode)
	useEffect(
		() => {
			setSummaryData((getState.error || getState.loading || !getState.data) ? null : getState.data)
		},
		[getState]
	)
	const onPosted = useCallback(
		(result: KojinSummaryResult) => {
			setSummaryData(result)
		},
		[setSummaryData]
	)
	const [postState, post] = usePostKojinSummary(search?.searchId, kojin?.kojinCode, {
		onPosted: onPosted
	})
	const onGenAI = useCallback(
		(result: string | null) => {
			if (result) {
				setEditData(result)
			}
		},
		[setEditData]
	)
	const [genAIState, genAI] = usePostKojinSummaryGenerativeAI(search?.searchId, kojin?.kojinCode, {
		onPosted: onGenAI
	})

	const onEditStart = useCallback(
		() => {
			setEditData(summaryData?.summary ?? '')
			setEditMode(true)
		},
		[summaryData, setEditData, setEditMode]
	)
	const onApplyEdit = useCallback(
		() => {
			post({
				summary: editData,
				updateTime: summaryData?.updateTime
			})
			setEditMode(false)
		},
		[editData, summaryData, setEditMode, post]
	)
	const onEditCancel = useCallback(
		() => {
			setEditMode(false)
		},
		[setEditMode]
	)

	return {
		isValid: (search?.searchId && kojin?.kojinCode && (summaryData || isGeneral)) ? true : false,
		isEdittable: isGeneral,
		loading: getState.loading || postState.posting || genAIState.posting,
		getError: getState.error,
		postError: postState.error,
		genAIError: genAIState.error,
		isEditMode, setEditMode,
		searchId: search?.searchId,
		summaryData,
		onEditStart, onApplyEdit, onEditCancel, editData, setEditData,
		genAI, hasGenAI
	}
}

type EditKojinSummaryProps = {
	summary: ReturnType<typeof useKojinSummary>
} & StackProps
const EditKojinSummary = ({
	summary,
	...others
}: EditKojinSummaryProps) => {
	return (
		<Stack {...others} direction="column" padding={1} spacing={1} height="100%" boxSizing="border-box">
			<MarkdownInput
				size="small"
				value={summary.editData}
				onChange={e => summary.setEditData(e.target.value)}
				fullWidth={true}
				fullHeight={true}
			/>
			<ErrorBlock error={summary.genAIError}/>
			<Stack direction="row" spacing={1}>
				{summary.hasGenAI && (
					<IconButton size="small" onClick={() => summary.genAI()}>
						<AIIcon color="primary"/>
					</IconButton>
				)}

				<Button
					variant="contained"
					size="small"
					onClick={summary.onApplyEdit}
					startIcon={<OkIcon/>}
				>
					保存
				</Button>
				<Button
					variant="text"
					size="small"
					onClick={summary.onEditCancel}
					startIcon={<CloseIcon/>}
				>
					キャンセル
				</Button>
			</Stack>
		</Stack>		
	)
}

type ViewKojinSummaryProps = {
	summary: ReturnType<typeof useKojinSummary>
} & StackProps
const ViewKojinSummary = ({
	summary,
	...others
}: ViewKojinSummaryProps) => {
	return summary.summaryData ? (
		<Stack {...others} direction="column">
			<BlockCaption
				caption="サマリー"
				rightElement={
					<Stack direction="row" spacing={1} alignItems="center">
						{summary.summaryData?.updateTime ? (
							<Typography variant="caption">{dateTimeStampFormat(summary.summaryData.updateTime)} 更新</Typography>
						) : <></>}

						{summary.isEdittable && (
							<Button
								variant="text"
								size="small"
								onClick={summary.onEditStart}
								startIcon={<EditIcon />}
							>
								サマリーを編集
							</Button>
						)}
					</Stack>
				}
			/>
			<Stack direction="column" spacing={1}>
				<ErrorBlock error={summary.postError}/>
				<ErrorBlock error={summary.genAIError}/>

				{summary.summaryData?.summary && (
					<MarkdownPreview
						value={summary.summaryData?.summary ?? ''}
						emptyText=""
						padding={2}
					/>
				)}
			</Stack>
		</Stack>
	) : summary.isEdittable ? (
		<Box padding={1}>
			<Button
				variant="text"
				size="small"
				fullWidth
				onClick={summary.onEditStart}
				startIcon={<EditIcon />}
			>
				サマリーを追加
			</Button>
		</Box>
	) : <></>
}

type KojinSummaryProps = {
	summary: ReturnType<typeof useKojinSummary>
} & BoxProps
export const KojinSummary = ({
	summary,
	...others
}: KojinSummaryProps) => {
	return (
		<Box height="100%" {...others}>
			{summary.isEditMode ? (
				<EditKojinSummary summary={summary}/>
			) : (summary.isEdittable || summary.summaryData?.summary) ? (
				<ViewKojinSummary summary={summary}/>
			) : <></>}
		</Box>
	)
}